import React, {
  CSSProperties,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {css} from "/src/utils/utils";

import * as styles from "./dropdown-menu.module.scss";

interface Props {
  bottomStyles?: CSSProperties; // inline styles that will be applied if the menu is rendered under the parent
  children: ReactNode;
  classes?: string;
  maxHeight?: number; // if the height of the dropdown should be limited. will be ignored if the available space is smaller than this value.
  offsetX?: number; // amount of vertical space between the menu and parent element. can be negative.
  offsetY?: number; // amount of horizontal space between the menu and parent element. can be negative.
  open: boolean;
  forceBottomAnchor?: boolean;
  parent: RefObject<HTMLDivElement>;
  restrictWidth?: boolean;
  toggle: (value: boolean) => void;
  topStyles?: CSSProperties; // inline styles that will be applied if the menu is rendered above the parent
}

const MARGIN = 8; // refers to how much space we should have between the element and edges of the viewport.

/**
 * This is a design-agnostic component that emulates the behaviour of a dropdown
 * and doesn't provide a trigger/toggle element.
 * All it does is calculate the space available on the screen to determine a
 * spot most ideal to place itself in. It also addresses overflow if its
 * content exceeds the space available.
 */
export default function DropdownMenu(props: Props) {
  const {
    bottomStyles,
    children,
    classes = "",
    maxHeight,
    offsetX = 0,
    offsetY = 0,
    open,
    parent,
    restrictWidth = false,
    forceBottomAnchor = false,
    toggle,
    topStyles,
  } = props;

  const $root = useRef<HTMLDivElement | null>(null);
  const $scroller = useRef<HTMLDivElement | null>(null);

  /**
   * Calculates available space on screen.
   * Returns an array of numbers in top-right-bottom-left order.
   */
  const getAvailableSpace = React.useCallback((): number[] => {
    if (parent.current == null) {
      return [0, 0, 0, 0];
    }

    const rect = parent.current.getBoundingClientRect();
    const top = rect.top + offsetY - MARGIN;
    const right = document.body.offsetWidth - rect.left - offsetX - MARGIN;
    const bottom = window.innerHeight - rect.bottom - offsetY - MARGIN;
    const left = rect.right - offsetX - MARGIN;

    return [top, right, bottom, left];
  }, [offsetX, offsetY, parent]);

  /**
   * Click event handler on Window.
   * Hide the menu if we're clicking outside of it.
   * @param {MouseEvent} e
   */
  const windowClickHandler = React.useCallback(
    (e: MouseEvent) => {
      if ($root.current && $root.current.contains(e.target as Node) === false) {
        // prevent accidental clicks
        e.stopPropagation();
        e.preventDefault();

        if (toggle != null) {
          toggle(false);
        }
      }

      return false;
    },
    [toggle]
  );

  /**
   * Key-up event handler on Window.
   * Collapses menu when the 'escape' key is pressed.
   * @param {KeyboardEvent} e
   */
  const windowKeyUpHandler = React.useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && toggle != null) {
        toggle(false);
      }
    },
    [toggle]
  );

  /**
   * In the event the user scrolls the page by clicking/dragging the scrollbar,
   * the wheel event handler will not be able to block scrolling and we shall
   * then just remove the menu.
   */
  const windowScrollHandler = React.useCallback(() => {
    if (toggle != null) {
      toggle(false);
    }
  }, [toggle]);

  /**
   * Wheel event handler on Window.
   * Prevent user from scrolling the window while the menu is expanded if:
   * - The scroll target is not the list or a child of the list
   * - The list is not scrollable at all.
   * - `scrollTop` of the list is at either end (0 or max).
   *
   * This emulates the behaviour of the native <select> element.
   */
  const windowWheelHandler = React.useCallback((e: WheelEvent) => {
    if ($root.current == null || !(e.target instanceof Node)) {
      return;
    }

    const targetIsChild = $root.current.contains(e.target);

    const listHasNoScroll =
      $scroller.current !== null &&
      $scroller.current.scrollHeight === $scroller.current.offsetHeight;

    const isScrollingBeyondTop =
      $scroller.current !== null &&
      $scroller.current.scrollTop === 0 &&
      e.deltaY < 0;

    const isScrollingBeyondBottom =
      $scroller.current !== null &&
      $scroller.current.offsetHeight + $scroller.current.scrollTop >=
        $scroller.current.scrollHeight &&
      e.deltaY > 0;

    if (
      !targetIsChild ||
      listHasNoScroll ||
      (targetIsChild && (isScrollingBeyondTop || isScrollingBeyondBottom))
    ) {
      // prevent scrolling behaviour from "overflowing" into main scrollbar
      // similar to:
      // https://developer.mozilla.org/en-US/docs/Web/CSS/overscroll-behavior
      e.preventDefault();
    }
  }, []);

  useEffect(() => {
    if (!open) {
      return () => {};
    }

    window.addEventListener("click", windowClickHandler, true);
    window.addEventListener("keyup", windowKeyUpHandler);
    window.addEventListener("scroll", windowScrollHandler, {passive: true});
    // window.addEventListener("touchmove", windowWheelHandler, {passive: false});
    window.addEventListener("wheel", windowWheelHandler, {passive: false});

    return () => {
      window.removeEventListener("click", windowClickHandler, true);
      window.removeEventListener("keyup", windowKeyUpHandler);
      window.removeEventListener("scroll", windowScrollHandler);
      // window.removeEventListener("touchmove", windowWheelHandler);
      window.removeEventListener("wheel", windowWheelHandler);
    };
  }, [
    open,
    windowClickHandler,
    windowKeyUpHandler,
    windowScrollHandler,
    windowWheelHandler,
  ]);

  useEffect(() => {
    if ($scroller.current == null) {
      return;
    }

    $scroller.current.inert = !open;
  }, [open, $scroller]);

  /**
   * Determines menu's position and size.
   * Returns an object containing CSS classes and inline styles.
   */
  const placementStyles = useCallback(() => {
    if (!open || !parent.current || $root.current == null) {
      return {};
    }

    const rect = parent.current.getBoundingClientRect();

    let inlineStyles: Record<string, any> = {};

    if (forceBottomAnchor) {
      inlineStyles.minWidth = `${rect.width - offsetX * 2}px`;
      inlineStyles.left = "-2px";
      inlineStyles.top = `${rect.height + offsetY - 1}px`;
      inlineStyles.maxHeight = "240px";

      if (bottomStyles != null) {
        inlineStyles = {...inlineStyles, ...bottomStyles};
      }

      return inlineStyles;
    }

    const [top, right, bottom, left] = getAvailableSpace();

    let xLimit = right;
    let yLimit = bottom;

    inlineStyles.minWidth = `${rect.width - offsetX * 2}px`;

    // this check is here instead of the top to appease flow
    if ($root.current == null) {
      return {};
    }

    // change horizontal position if necessary
    if ($root.current.offsetWidth > right && left > right) {
      xLimit = left;
      inlineStyles.right = `calc(100% - ${rect.right - offsetX}px)`;
    } else {
      inlineStyles.left = `${rect.left + offsetX}px`;
    }

    const tempHeight =
      maxHeight != null ? maxHeight : $root.current.offsetHeight;
    const placeAbove = tempHeight > bottom && top > bottom;

    // change vertical position if necessary
    if (placeAbove) {
      yLimit = top;
      inlineStyles.bottom = `${Math.round(
        window.innerHeight - rect.top + offsetY
      )}px`;

      if (topStyles != null) {
        inlineStyles = {...inlineStyles, ...topStyles};
      }
    } else {
      inlineStyles.top = `${Math.round(rect.bottom + offsetY)}px`;

      if (bottomStyles != null) {
        inlineStyles = {...inlineStyles, ...bottomStyles};
      }
    }

    // restrict width
    inlineStyles.maxWidth =
      restrictWidth === true
        ? `${rect.width - offsetX * 2}px`
        : `${Math.round(xLimit)}px`;

    // restrict height
    inlineStyles.maxHeight =
      maxHeight != null && maxHeight < yLimit
        ? `${Math.round(maxHeight)}px`
        : `${Math.round(yLimit)}px`;

    return inlineStyles;
  }, [
    open,
    parent,
    forceBottomAnchor,
    getAvailableSpace,
    offsetX,
    maxHeight,
    restrictWidth,
    offsetY,
    bottomStyles,
    topStyles,
  ]);

  return (
    <div
      className={css(
        styles.dropdownMenu,
        forceBottomAnchor ? styles.dropdownMenu__bottom : "",
        open ? styles.dropdownMenuOpen : "",
        classes
      )}
      ref={$root}
      data-name="DropdownMenu"
      style={placementStyles()}
    >
      <div ref={$scroller} className={styles.dropdownMenu__scroller}>
        {children}
      </div>
    </div>
  );
}
